import React from "react";
import { useI18n, pickLocalized } from "@/lib/i18n";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import { ClipboardCopy } from "lucide-react";
import { buildCitation, copyToClipboard } from "@/lib/brand-citation";

/**
 * Bottom-of-page Accordion block for a brand profile.
 *
 * Renders (conditionally, in order):
 *   1. Attempted URLs — only when the profile is `access_restricted` and has recorded attempts
 *   2. Data trail — provenance sentences (how the pipeline discovered pages)
 *   3. Sources — every URL that fed evidence
 *   4. Documents — PDF documents with APA "Cite" button
 *   5. Changelog — profile history
 *
 * Extracted from BrandProfile.jsx to keep the container focused on data
 * fetching and page-level SEO. All test-ids preserved exactly.
 */
export default function ProfileAccordion({ brand, profile, sources, documents, changelog, dataTrail, accessRestricted }) {
  const { t, locale } = useI18n();

  return (
    <Accordion type="multiple" className="w-full">
      {accessRestricted && profile?.attempted_urls && profile.attempted_urls.length > 0 && (
        <AccordionItem value="attempted-urls">
          <AccordionTrigger data-testid="attempted-urls-toggle" className="font-serif text-xl">
            {t.access_restricted_attempted_title} ({profile.attempted_urls.length})
          </AccordionTrigger>
          <AccordionContent>
            <ul className="space-y-3 text-sm" data-testid="attempted-urls-list">
              {profile.attempted_urls.map((u, i) => {
                const bucketLabel =
                  u.reason_bucket === "robots_txt_disallow" ? t.access_restricted_robots_label :
                  u.reason_bucket === "cdn_challenge" ? t.access_restricted_cdn_label :
                  t.access_restricted_other_label;
                const detail = u.reason_bucket === "robots_txt_disallow"
                  ? (u.reason_detail || "").replace(/^robots_txt_disallow:/, "")
                  : "";
                return (
                  <li key={`${u.url}-${i}`} className="border-l-2 pl-3" style={{ borderLeftColor: u.reason_bucket === "robots_txt_disallow" ? "#5A7A9A" : "#B97700" }} data-testid={`attempted-url-${i}`}>
                    <div className="flex flex-wrap items-baseline gap-2 mb-1">
                      <span className="uppercase text-[10px] tracking-widest px-1.5 py-0.5 border font-mono" style={{ borderColor: "var(--bp-border)", color: "var(--bp-text-muted)" }}>
                        {bucketLabel}
                      </span>
                      <span className="text-xs font-mono" style={{ color: "var(--bp-text-muted)" }}>
                        {String(u.attempted_at || "").slice(0, 10)}
                      </span>
                    </div>
                    <div className="break-all font-mono text-xs">{u.url}</div>
                    {detail && (
                      <div className="mt-1 font-mono text-xs" style={{ color: "var(--bp-text-muted)" }}>
                        <code>{detail}</code>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </AccordionContent>
        </AccordionItem>
      )}

      {dataTrail && dataTrail.sentences && dataTrail.sentences.length > 0 && (
        <AccordionItem value="data-trail">
          <AccordionTrigger data-testid="data-trail-toggle" className="font-serif text-xl">Data trail</AccordionTrigger>
          <AccordionContent>
            <div className="text-sm" style={{ color: "var(--bp-text-secondary)" }} data-testid="data-trail-content">
              <p className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: "var(--bp-text-muted)" }}>
                How the pipeline found the pages behind this profile
              </p>
              <ul className="space-y-1">
                {dataTrail.sentences.map((s, i) => (
                  <li key={`${(s || "").slice(0, 40)}-${i}`} data-testid={`data-trail-line-${i}`} className="before:content-['·'] before:mr-2 before:opacity-50">{s}</li>
                ))}
              </ul>
              <p className="text-xs mt-4" style={{ color: "var(--bp-text-muted)" }}>
                Factual provenance — no scoring, no ranking. Each page above was read verbatim and only claims that
                appear literally on the page are stored as evidence.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>
      )}

      <AccordionItem value="sources">
        <AccordionTrigger data-testid="sources-toggle" className="font-serif text-xl">{t.sources_title} ({sources.length})</AccordionTrigger>
        <AccordionContent>
          <ul className="space-y-2 text-sm font-mono">
            {sources.map((s, i) => {
              const isPdf = (s.url || "").toLowerCase().endsWith(".pdf");
              return (
                <li key={`${s.category}-${s.url}-${i}`} className="flex flex-wrap gap-3 items-center">
                  <span className="uppercase text-xs tracking-widest" style={{ color: "var(--bp-text-muted)" }}>{s.category}</span>
                  {isPdf && (
                    <span className="uppercase text-[10px] tracking-widest px-1.5 py-0.5 bg-neutral-900 text-white" title="PDF document">PDF</span>
                  )}
                  <a href={s.url} target="_blank" rel="noopener noreferrer" className="bp-link">{s.url}</a>
                  <span style={{ color: "var(--bp-text-muted)" }}>· <time>{(s.retrieved_at || "").slice(0,10)}</time></span>
                </li>
              );
            })}
          </ul>
        </AccordionContent>
      </AccordionItem>

      {documents && documents.length > 0 && (
        <AccordionItem value="documents">
          <AccordionTrigger data-testid="documents-toggle" className="font-serif text-xl">{t.documents_title} ({documents.length})</AccordionTrigger>
          <AccordionContent>
            <p className="text-sm mb-4" style={{ color: "var(--bp-text-secondary)" }}>{t.documents_intro}</p>
            <ul className="space-y-3 text-sm" data-testid="documents-list">
              {documents.map((d, i) => {
                let host = "";
                try { host = new URL(d.url).hostname.replace(/^www\./, ""); } catch { host = ""; }
                const cite = async () => {
                  const citation = buildCitation({
                    brandName: brand.display_name || brand.legal_name || "",
                    title: d.title,
                    url: d.url,
                    fetchedAt: d.fetched_at,
                  });
                  const ok = await copyToClipboard(citation);
                  if (ok) toast.success(t.cite_copied, { description: citation });
                  else toast.error(t.cite_failed);
                };
                return (
                  <li key={`${d.url}-${i}`} className="flex items-start gap-3" data-testid={`document-${i}`}>
                    <span className="uppercase text-[10px] tracking-widest px-1.5 py-0.5 mt-0.5 bg-neutral-900 text-white font-mono" title="PDF document">PDF</span>
                    <div className="flex-1 min-w-0">
                      <a href={d.url} target="_blank" rel="noopener noreferrer" className="bp-link font-serif break-all">
                        {d.title || d.url}
                      </a>
                      <div className="text-xs font-mono mt-0.5" style={{ color: "var(--bp-text-muted)" }}>
                        {host}
                        {d.discovery_lane && <> · {d.discovery_lane} lane</>}
                        {d.fetched_at && <> · read {String(d.fetched_at).slice(0, 10)}</>}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={cite}
                      title="Copy APA-style citation"
                      className="text-xs inline-flex items-center gap-1 px-2 py-1 border hover:bg-neutral-100 transition-colors"
                      style={{ borderColor: "var(--bp-border)" }}
                      data-testid={`cite-btn-${i}`}
                    >
                      <ClipboardCopy size={12} />
                      {t.cite_button}
                    </button>
                  </li>
                );
              })}
            </ul>
          </AccordionContent>
        </AccordionItem>
      )}

      <AccordionItem value="changelog">
        <AccordionTrigger data-testid="changelog-toggle" className="font-serif text-xl">{t.changelog_title} ({changelog.length})</AccordionTrigger>
        <AccordionContent>
          <ul className="space-y-3 text-sm">
            {changelog.map((c) => (
              <li key={c.id} className="flex flex-wrap items-baseline gap-3">
                <time className="font-mono text-xs" style={{ color: "var(--bp-text-muted)" }}>{(c.changed_at || "").slice(0, 10)}</time>
                <span className="uppercase text-xs tracking-widest">{c.change_type.replace(/_/g, " ")}</span>
                {c.public_note && <span style={{ color: "var(--bp-text-secondary)" }}>{pickLocalized(c.public_note, locale)}</span>}
              </li>
            ))}
          </ul>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
